import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import useAssistantActionHandlers from './use-assistant-action-handlers';
import { Actionable } from '../models/assistant/interfaces/Actionable';

/**
 * Chatbot-specific action handlers that use replace() instead of push()
 * for internal navigation to avoid creating unwanted history entries.
 *
 * This hook extends the base assistant action handlers and only customizes
 * the internal navigation behavior.
 */
export default function useChatbotActionHandlers() {
  const history = useHistory();
  const baseHandlers = useAssistantActionHandlers();

  const handleAction = useCallback(
    (action: Actionable | undefined, analyticsData?: unknown) => {
      if (!action) return;

      // For internal navigation, use replace() instead of push()
      if (
        action.type === 'navigation' &&
        action.navigation.type === 'internal'
      ) {
        // Use replace instead of push to avoid adding to history stack
        // This creates a clean navigation: /assistant -> /assistant/commands/internal
        // without the intermediate /assistant/commands page
        history.replace('/assistant/commands/internal', {
          renderUrl: action.navigation.url,
          analyticsData,
        });
        return undefined;
      }

      // For all other action types, delegate to the base handler
      return baseHandlers.handleAction(action, analyticsData);
    },
    [history, baseHandlers]
  );

  return {
    getDeeplink: baseHandlers.getDeeplink,
    handleAction,
    handleRequest: baseHandlers.handleRequest,
  };
}
